import { addDays, addMinutes, subMinutes } from 'date-fns';
import { defineMessages } from 'react-intl';
import _range from 'lodash/range';

export const messages = defineMessages({
  dayRange: {
    id: 'manage.smart-tools.peak-definition-day-range',
    defaultMessage: '{first}-{last}',
  },
});

const ISO_MONDAY = 1;
const ISO_SUNDAY = 7;
const SHORTEST_DAY_RANGE = 3;
const TIME_FORMAT = { hour: 'numeric', minute: 'numeric' };

// A Monday, so addDays(REFERENCE_MONDAY, isoDay - 1) lands on the matching weekday.
const REFERENCE_MONDAY = new Date(2024, 0, 1);

const toMinutes = ({ hours = 0, minutes = 0 } = {}) => hours * 60 + minutes;

const toDate = timeOfDay => addMinutes(REFERENCE_MONDAY, toMinutes(timeOfDay));

const byStartTime = (a, b) => toMinutes(a.start_time) - toMinutes(b.start_time);

const fingerprint = sortedWindows =>
  sortedWindows.map(({ start_time: start, end_time: end }) => `${toMinutes(start)}-${toMinutes(end)}`).join('|');

const consecutiveRuns = days =>
  days.reduce((runs, day) => {
    const openRun = runs[runs.length - 1];

    if (openRun && day === openRun[openRun.length - 1] + 1) {
      openRun.push(day);
    } else {
      runs.push([day]);
    }

    return runs;
  }, []);

export const selectEffectiveWindows = definitions => {
  if (!definitions) {
    return [];
  }

  return definitions.venue_windows?.length ? definitions.venue_windows : definitions.default_windows || [];
};

export const groupWindowsByDay = (windows = []) => {
  const windowsByDay = new Map();

  windows.forEach(window => {
    const day = window.day_of_week;

    if (day >= ISO_MONDAY && day <= ISO_SUNDAY) {
      windowsByDay.set(day, [...(windowsByDay.get(day) || []), window]);
    }
  });

  const groups = new Map();

  _range(ISO_MONDAY, ISO_SUNDAY + 1).forEach(day => {
    const dayWindows = windowsByDay.get(day);

    if (!dayWindows) {
      return;
    }

    const sorted = [...dayWindows].sort(byStartTime);
    const key = fingerprint(sorted);
    const group = groups.get(key);

    if (group) {
      group.days.push(day);
    } else {
      groups.set(key, { days: [day], windows: sorted });
    }
  });

  return [...groups.values()];
};

export const formatDayList = (intl, days) => {
  const shortDayName = day => intl.formatDate(addDays(REFERENCE_MONDAY, day - ISO_MONDAY), { weekday: 'short' });

  const tokens = consecutiveRuns(days).flatMap(run =>
    run.length >= SHORTEST_DAY_RANGE
      ? intl.formatMessage(messages.dayRange, { first: shortDayName(run[0]), last: shortDayName(run[run.length - 1]) })
      : run.map(shortDayName)
  );

  return intl.formatList(tokens, { type: 'unit' });
};

export const formatWindowTimes = (intl, windows) =>
  intl.formatList(
    windows.map(({ start_time: start, end_time: end }) =>
      intl.formatDateTimeRange(toDate(start), subMinutes(toDate(end), 1), TIME_FORMAT)
    ),
    { type: 'unit' }
  );
